/**
 * Haptic feedback.
 *
 * Bot API 6.1+. Below that the SDK warns and no-ops, so the version gate here
 * only keeps an old client's console quiet — but it is the same gate every
 * other capability uses, and one exception would be the one somebody copies.
 *
 * Outside Telegram every call is a no-op, so callers never branch on it.
 *
 * Cheap, and most of what makes a webview feel like an app rather than a page
 * rendered inside one.
 */

import {
  callSafely,
  isVersionAtLeast,
  webAppSdk,
  type HapticNotification,
  type HapticStyle,
} from './webapp.ts';

function available() {
  const app = webAppSdk();
  return app && isVersionAtLeast('6.1') ? app : null;
}

export const haptics = {
  /** A tab was switched, a row selected. The lightest one. */
  selection(): void {
    const app = available();
    if (app) callSafely('haptics.selection', () => app.HapticFeedback.selectionChanged());
  },

  /**
   * An outcome landed: a save succeeded, a destructive action was confirmed,
   * a request failed. Distinct from `impact` — this one has meaning attached.
   */
  notify(type: HapticNotification): void {
    const app = available();
    if (app) callSafely('haptics.notify', () => app.HapticFeedback.notificationOccurred(type));
  },

  /** A physical-feeling collision: a button press, a sheet hitting its stop. */
  impact(style: HapticStyle = 'light'): void {
    const app = available();
    if (app) callSafely('haptics.impact', () => app.HapticFeedback.impactOccurred(style));
  },
};
